import {
  Card,
  BlockStack,
  InlineStack,
  InlineGrid,
  Text,
  Badge,
  Banner,
  Button,
  Divider,
  Box,
} from "@shopify/polaris";
import { useTranslation } from "../hooks/useTranslation";

interface PlanInfo {
  id: string;
  name: string;
  price: number;
  quota: number;
  currency: string;
}

interface SubscriptionCardProps {
  subscriptionPlan: string | null;
  subscriptionStatus: string;
  isTrial: boolean;
  billingCycleStart: string | null;
  /** Paid-through date; for a cancelled shop it is when access ends. */
  billingCycleEnd?: string | null;
  submissionsRemaining?: number;
  onUpgrade: (plan: string) => void;
  onCancel: () => void;
  isLoading: boolean;
}

const PLANS: PlanInfo[] = [
  { id: "free", name: "Free Trial", price: 0, quota: 7, currency: "EUR" },
  { id: "comply-starter", name: "Starter", price: 9.99, quota: 30, currency: "EUR" },
  { id: "comply-professional", name: "Professional", price: 14.99, quota: 60, currency: "EUR" },
  { id: "comply-growth", name: "Growth", price: 29.99, quota: 300, currency: "EUR" },
];

export function SubscriptionCard({
  subscriptionPlan,
  subscriptionStatus,
  isTrial,
  billingCycleStart,
  billingCycleEnd = null,
  submissionsRemaining,
  onUpgrade,
  onCancel,
  isLoading,
}: SubscriptionCardProps) {
  const { t } = useTranslation();

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Normalize legacy plan names
  const currentPlanId =
    subscriptionPlan === "basic" || subscriptionPlan === "starter"
      ? "comply-starter"
      : subscriptionPlan;

  const getStatusBadge = () => {
    if (isTrial) {
      return <Badge tone="info">{t("dashboard.subscription.status.trial")}</Badge>;
    }
    switch (subscriptionStatus) {
      case "active":
        return <Badge tone="success">{t("dashboard.subscription.status.active")}</Badge>;
      case "cancelled":
        return <Badge tone="warning">{t("dashboard.subscription.status.cancelled")}</Badge>;
      case "expired":
        return <Badge tone="critical">{t("dashboard.subscription.status.expired")}</Badge>;
      default:
        return <Badge tone="info">{t("dashboard.subscription.status.trial")}</Badge>;
    }
  };

  const handleCancelClick = () => {
    if (
      window.confirm(
        t("dashboard.subscription.confirmCancel") ||
        "Are you sure you want to cancel your subscription?",
      )
    ) {
      onCancel();
    }
  };

  // Show plan selector when trial exhausted, cancelled, or expired
  // Hide plans while user still has free trial submissions available
  const trialExhausted = isTrial && (submissionsRemaining === 0);
  const showPlanSelector = trialExhausted || subscriptionStatus === "cancelled" || subscriptionStatus === "expired";

  // Cancelled in Shopify but already paid: access continues until billingCycleEnd
  const accessUntil =
    subscriptionStatus === "cancelled" && billingCycleEnd && new Date(billingCycleEnd) > new Date()
      ? billingCycleEnd
      : null;

  return (
    <Card>
      <BlockStack gap="400">
        {/* Header */}
        <InlineStack align="space-between" blockAlign="center">
          <Text variant="headingMd" as="h2">
            {t("dashboard.subscription.title")}
          </Text>
          {/* Only show badge for active/cancelled/expired — not for trial */}
          {!isTrial && getStatusBadge()}
        </InlineStack>

        {accessUntil && (
          <Banner tone="warning">
            {t("dashboard.subscription.cancelledBanner", { date: formatDate(accessUntil) })}
          </Banner>
        )}

        {/* Active subscription display */}
        {subscriptionStatus === "active" && !isTrial && (
          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center">
              <BlockStack gap="100">
                <Text variant="headingLg" as="p">
                  {PLANS.find((p) => p.id === currentPlanId)?.name || "Starter"}
                </Text>
                <Text variant="bodyMd" as="p" tone="subdued">
                  €{PLANS.find((p) => p.id === currentPlanId)?.price || "9.99"}/
                  {t("dashboard.subscription.month")}
                  {" · "}
                  {PLANS.find((p) => p.id === currentPlanId)?.quota || 30}{" "}
                  {t("dashboard.subscription.docsPerMonth")}
                </Text>
              </BlockStack>
              <Button
                tone="critical"
                onClick={handleCancelClick}
                loading={isLoading}
              >
                {t("dashboard.subscription.actions.cancel")}
              </Button>
            </InlineStack>

            {billingCycleStart && (
              <>
                <Divider />
                <Text variant="bodySm" as="p" tone="subdued">
                  {t("dashboard.subscription.billingPeriod")}: {formatDate(billingCycleStart)}
                </Text>
              </>
            )}

            {/* Upgrade option if not on Growth */}
            {currentPlanId !== "comply-growth" && (
              <>
                <Divider />
                <InlineStack align="space-between" blockAlign="center">
                  <Text variant="bodyMd" as="p">
                    {t("dashboard.subscription.needMore")}
                  </Text>
                  <Button
                    variant="primary"
                    onClick={() => {
                      const nextPlan =
                        currentPlanId === "comply-starter"
                          ? "comply-professional"
                          : "comply-growth";
                      onUpgrade(nextPlan);
                    }}
                    loading={isLoading}
                  >
                    {t("dashboard.subscription.actions.upgrade")}
                  </Button>
                </InlineStack>
              </>
            )}
          </BlockStack>
        )}

        {/* Plan selector for exhausted trial/cancelled/expired */}
        {showPlanSelector && (
          <BlockStack gap="400">
            {isTrial && (
              <Text variant="bodyMd" as="p" tone="subdued">
                {t("dashboard.subscription.trialInfo")}
              </Text>
            )}

            <InlineGrid columns={{ xs: 1, sm: 3 }} gap="300">
              {PLANS.filter((p) => p.id !== "free").map((plan) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  isPopular={plan.id === "comply-professional"}
                  onSelect={() => onUpgrade(plan.id)}
                  isLoading={isLoading}
                  t={t}
                />
              ))}
            </InlineGrid>
          </BlockStack>
        )}
      </BlockStack>
    </Card>
  );
}

function PlanCard({
  plan,
  isPopular,
  onSelect,
  isLoading,
  t,
}: {
  plan: PlanInfo;
  isPopular: boolean;
  onSelect: () => void;
  isLoading: boolean;
  t: (key: string) => string;
}) {
  return (
    <Box
      padding="400"
      borderWidth="025"
      borderColor={isPopular ? "border-emphasis" : "border"}
      borderRadius="200"
      background={isPopular ? "bg-surface-secondary" : "bg-surface"}
    >
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <Text variant="headingMd" as="h3">
            {plan.name}
          </Text>
          {isPopular && (
            <Badge tone="info">{t("dashboard.subscription.popular")}</Badge>
          )}
        </InlineStack>

        <BlockStack gap="100">
          <InlineStack gap="100" blockAlign="baseline">
            <Text variant="headingXl" as="p">
              €{plan.price}
            </Text>
            <Text variant="bodyMd" as="span" tone="subdued">
              /{t("dashboard.subscription.month")}
            </Text>
          </InlineStack>
          <Text variant="bodyMd" as="p" tone="subdued">
            {plan.quota} {t("dashboard.subscription.docsPerMonth")}
          </Text>
        </BlockStack>

        <Text variant="bodySm" as="p" tone="subdued">
          €{(plan.price / plan.quota).toFixed(2)}/{t("dashboard.subscription.perDoc")}
        </Text>

        <Button
          variant={isPopular ? "primary" : "secondary"}
          fullWidth
          onClick={onSelect}
          loading={isLoading}
        >
          {t("dashboard.subscription.actions.select")}
        </Button>
      </BlockStack>
    </Box>
  );
}
